import { Epic } from "../state/reducer";
import { addStory, setEditModeItem, updateEpic } from "../state/actions";
import { useMemo, useRef, useState, useEffect } from "react";

import { styled } from "@compiled/react";
import { useIntl, MessageDescriptor } from "react-intl";
import sanitizeHtml from "sanitize-html";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import EditorAddIcon from "@atlaskit/icon/core/add";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import { B100, N50 } from "@atlaskit/theme/colors";

import { messages } from "../messages";
import { useAppDispatch, useAppSelector } from "../state/hook";

import { Epic16IconCustom } from "./epic-16-icon-custom";
import { HiddenAriaDescription } from "./hidden-aria-description";
import { EditContainer, IconBlock, Input, Name } from "./shared.styles";

/**
 * A JSX component for the epic column row.
 * @param param0
 * @returns A roadmap column epic column row.
 */
export const EpicRowItem = ({
    id,
    name,
    epicIndex,
    canClickAddButton
}: EpicColumnRowPropsType) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const isDemoComplete = useAppSelector(
        ({ isDemoComplete }) => isDemoComplete
    );
    const editModeItemId = useAppSelector(
        ({ editModeItemId }) => editModeItemId
    );
    const [epicNameInputValue, setEpicNameInputValue] = useState("");
    const [epicName, setEpicName] = useState(name);
    const epics = useAppSelector(({ epics }) => epics);
    const [rowMode, setRowMode] = useState("UNKNOWN" as ContentMode);
    const epicLabelText = useMemo(() => intl.formatMessage(messages.epic), []);
    const readOnlyTextValue = useMemo(
        () =>
            typeof epicName === "object"
                ? intl.formatMessage(epicName)
                : epicName,
        [epicName]
    );
    const activateToEditTextDescription = useMemo(
        () => intl.formatMessage(messages.activateToEditTextDescription),
        []
    );
    const activateToToggleMessageId = useMemo(
        () => `activateToToggleMessage-${id}`,
        [id]
    );
    useEffect(() => setEpicName(name), [name]);

    useEffect(
        () => setRowMode(editModeItemId === id ? "EDIT" : "VIEW"),
        [editModeItemId]
    );

    const updateEpicName = (updatedName: string) => {
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "interactiveRoadmapUpdateEpic",
            eventType: "ui"
        }).fire();

        dispatch(updateEpic({ epicName: updatedName, epicIndex }));
        dispatch(setEditModeItem({ id: undefined }));
        setEpicName(updatedName);
        setEpicNameInputValue("");
        setRowMode("VIEW");
    };

    const toggleEditMode = () => {
        // the demo has not been complete so the user cannot edit any story / epic, skip.
        if (!isDemoComplete) {
            return;
        }

        // there is another item in edit mode, skip.
        if (editModeItemId) {
            return;
        }

        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "interactiveRoadmapSetEditModeItem",
            eventType: "ui",
            attributes: {
                itemEditId: id
            }
        }).fire();

        dispatch(setEditModeItem({ id }));
        setRowMode("EDIT");

        if (typeof epicName === "object") {
            setEpicNameInputValue(intl.formatMessage(epicName));
        } else {
            setEpicNameInputValue(epicName);
        }
    };

    // auto focus on input element
    const containerRef = useRef(null as HTMLInputElement | null);
    const inputRef = useRef(null as HTMLInputElement | null);
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.onfocus = () => {
                if (containerRef.current) {
                    containerRef.current.style.borderColor = B100;
                }
            };

            inputRef.current.onblur = () => {
                if (containerRef.current) {
                    containerRef.current.style.borderColor = N50;
                }
            };

            inputRef.current.focus({ preventScroll: true });
        }
    }, [rowMode, inputRef, containerRef]);

    const readOnlyContent = (
        <ReadOnlyContainer data-testid="read-only-epic-row-item">
            <HiddenAriaDescription
                id={activateToToggleMessageId}
                message={activateToEditTextDescription}
            />
            <Block
                type="button"
                data-testid="read-only-epic-row-item-block"
                onClick={toggleEditMode}
                aria-describedby={activateToToggleMessageId}
                aria-disabled={!isDemoComplete || editModeItemId != null}
            >
                <IconBlock>
                    <ChevronDownIcon label="" />
                    <Epic16IconCustom
                        primaryColor="#BF63F3"
                        label={epicLabelText}
                    />
                </IconBlock>
                <Name>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(readOnlyTextValue)
                        }}
                    />
                </Name>
            </Block>
            {canClickAddButton && !editModeItemId && (
                <IconBlock>
                    {/* eslint-disable-next-line styled-components-a11y/no-autofocus */}
                    <AddButton
                        autoFocus
                        aria-label={intl.formatMessage(messages.addChild)}
                        data-testid="add-epic-child-button"
                        onClick={() => {
                            createAnalyticsEvent({
                                action: "clicked",
                                actionSubject: "button",
                                actionSubjectId:
                                    "interactiveRoadmapAddStoryToEpic",
                                eventType: "ui",
                                attributes: {
                                    numOfChildStories:
                                        (epics[1] as Epic).children?.length + 1
                                }
                            }).fire();
                            dispatch(addStory());
                        }}
                    >
                        <EditorAddIcon label="" />
                    </AddButton>
                </IconBlock>
            )}
        </ReadOnlyContainer>
    );

    const editContent = (
        <EditContainer data-testid="edit-epic-row-item" ref={containerRef}>
            <IconBlock>
                <Epic16IconCustom
                    primaryColor="#BF63F3"
                    label={epicLabelText}
                />
            </IconBlock>
            <Input
                data-testid="edit-input-epic-row-item"
                ref={inputRef}
                value={epicNameInputValue}
                onChange={(changeEvent) =>
                    setEpicNameInputValue(changeEvent.target.value)
                }
                placeholder={intl.formatMessage(messages.updateEpicPlaceholder)}
                onKeyDown={(keyEvent) => {
                    if (
                        keyEvent.key === "Enter" &&
                        epicNameInputValue.length > 0
                    ) {
                        updateEpicName(epicNameInputValue);

                        // prevents key event from drilling down to addStory button, causing the addStory action to dispatch.
                        keyEvent.preventDefault();
                    }
                }}
                onBlur={() => {
                    if (epicNameInputValue.length > 0) {
                        updateEpicName(epicNameInputValue);
                    }
                }}
            />
        </EditContainer>
    );

    return rowMode === "EDIT" ? editContent : readOnlyContent;
};

const Block = styled.button({
    backgroundColor: "transparent",
    border: "none",
    flex: 1,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "8px"
});

const AddButton = styled.button({
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "24px",
    height: "24px",
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "#e0e1e6",
    borderRadius: "3px",
    marginRight: "25px",
    border: "none"
});

const ReadOnlyContainer = styled.div({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    borderRadius: "4px",
    margin: "0px 8px",
    transition: "transform 1s ease-in",
    maxWidth: "406px",
    gap: "9px"
});

export type EpicColumnRowPropsType = {
    id: string;
    name: string | MessageDescriptor;
    epicIndex: number;
    canClickAddButton: boolean | undefined;
};

export type ContentMode = "EDIT" | "VIEW" | "UNKNOWN";
